import { useEffect, useMemo } from 'react';
import { useLocation } from 'react-router';

import { useDepartmentStore } from '../stores';
import { DepartmentResponse } from '../types';
import { departmentsService } from '../../departments/services/departments.service';

import { FunctionName, PermissionAction } from '@/lib/enums';
import { getFunctionNameFromPathname } from '@/utils/helpers';
// API call to fetch departments
const fetchDepartments = async (
  functionName: FunctionName,
  permissionAction?: PermissionAction
): Promise<DepartmentResponse[]> => {
  const response = await departmentsService.getDepartments(functionName, permissionAction);
  if (!response) {
    throw new Error('Failed to fetch departments');
  }
  // API response structure: { data: [...], timestamp, requestId }
  return response;
};

export const useDepartments = (permissionAction?: PermissionAction) => {
  const {
    departments,
    loading,
    error,
    setDepartments,
    setLoading,
    setError,
    getDepartmentById,
    getDepartmentByCode,
    clearDepartments,
  } = useDepartmentStore();

  const location = useLocation();

  const functionName = useMemo(
    () => getFunctionNameFromPathname(location.pathname),
    [location.pathname]
  );
  const loadDepartments = async (fn: FunctionName) => {
    try {
      setLoading(true);
      const data = await fetchDepartments(fn, permissionAction);
      setDepartments(data);
    } catch (err) {
      console.error('Error loading departments:', err);
      setError(err instanceof Error ? err.message : 'Failed to load departments');
    } finally {
      setLoading(false);
    }
  };

  // Always refetch when path/function changes so staff department changes are reflected
  useEffect(() => {
    void loadDepartments(functionName);
  }, [functionName]);

  return {
    departments,
    loading,
    error,
    loadDepartments,
    getDepartmentById,
    getDepartmentByCode,
    clearDepartments,
  };
};
